import { useRef, useState } from 'react';
import { useFormContext } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router-dom';

import { AI_ASSISTANT_ITEMS, AI_RESPONSE_TYPES, IAIAssistantType, IAIResponseType } from 'constants/AI';
import { BUSINESS_PLAN_TYPES } from 'constants/businessPlan';
import { DIMENSIONALITY_VALUES } from 'constants/businessTool';
import { BusinessPlanAIApi, isProgrammaticAIRequestAbort } from 'services/businessPlanAI/businessPlanAI.api';
import { IBusinessPlanAIFunction } from 'services/businessPlanAI/types';
import { useCreateProduct, useDeleteProduct } from 'services/calculations/globalProducts/hooks';
import { useGetCalculationsOnProductUpdate } from 'services/calculations/salesPlan/hooks';
import { useBusinessToolStore } from 'store/useBusinessToolStore';
import {
  getAIAssistantReadinessPercent,
  getBusinessPlanChapters,
  getBusinessPlanType,
} from 'store/useBusinessToolStore/selectors';

import { AIGenerationErrorModalParent } from 'pages/BusinessToolPage/components/AIGenerationErrorModalParent/AIGenerationErrorModalParent';

import { DEFAULT_VALUE } from 'components/_basic/Form/NumberInput/NumberInputControlled/NumberInputControlled';
import { unitConvertorMultiplier } from 'components/BusinessToolForm/components/CalculationsObserver/helpers/balanceSheet/consts';
import {
  getProductFieldsForSalesPlan,
  isAllSalesPlanRequiredFieldsFilled,
} from 'components/BusinessToolForm/components/CalculationsObserver/helpers/salesPlan/salesPlanCalculation';
import { useOrderTableValues } from 'components/BusinessToolForm/components/CalculationsObserver/hooks/useOrderTableValues';
import { IGoalAndObjectiveRowForm } from 'components/BusinessToolForm/Sections/AboutCompany/MissionAndVision/utils/form';
import { GLOBAL_FIELD_NAMES, GLOBAL_UNIT_NAME } from 'components/BusinessToolForm/Sections/Global/form';
import { IListOfRisks } from 'components/BusinessToolForm/Sections/LegalFrameworkAndRisks/utils/form';
import {
  COMPANY_QUALITIES_NAMES,
  COMPETITIVE_ANALYSIS_NAMES,
  ICompanyQualities,
  IKeyCompetitors,
  STRENGTH_NAMES,
  WEAKNESSES_NAMES,
} from 'components/BusinessToolForm/Sections/MarketAnalysis/CompetitiveAnalysis/utils/form';
import { prefix as marketAnalysisPrefix } from 'components/BusinessToolForm/Sections/MarketAnalysis/CompetitiveAnalysis/utils/form';
import { B2C_NAMES } from 'components/BusinessToolForm/Sections/MarketAnalysis/ConsumerNeeds/utils/form';
import {
  IMarketTrendsForm,
  MARKET_TRENDS_FIELD_NAMES,
} from 'components/BusinessToolForm/Sections/MarketAnalysis/MarketAnalysisOverview/utils/form';
import {
  IChannels,
  ISalesStages,
} from 'components/BusinessToolForm/Sections/MarketingAndSales/SalesPlanSection/utils/form';
import { useModalContext } from 'components/ModalManager';

import { checkIsMarketOverviewOrCompetitiveAnalysisField } from 'helpers/checkIsMarketOverviewOrCompetitiveAnalysisField';
import { asyncDelay } from 'helpers/delay';
import ToastService from 'helpers/ToastService';

import {
  AI_ASSISTANT_ERRORS,
  createAIErrorObject,
  IAIAssistantError,
  IAIAssistantErrorTypes,
  useAiErrors,
} from './helpers';

const aiOption: Record<IAIAssistantType, IBusinessPlanAIFunction> = {
  [AI_ASSISTANT_ITEMS.GENERATE]: BusinessPlanAIApi.generateField,
  [AI_ASSISTANT_ITEMS.IMPROVE]: BusinessPlanAIApi.improveField,
  [AI_ASSISTANT_ITEMS.SHORTER]: BusinessPlanAIApi.shorter,
  [AI_ASSISTANT_ITEMS.LONGER]: BusinessPlanAIApi.longer,
  [AI_ASSISTANT_ITEMS.FIX_GRAMMAR]: BusinessPlanAIApi.fixGrammar,
};

export const useBusinessPlanAIFieldUpdate = (
  fieldName?: string | string[],
  speed = 50,
  fieldType?: IAIResponseType,
  isTypingAnimationEnabled: boolean = true,
) => {
  const { id: businessPlanId = '' } = useParams();
  const AIAssistantReadinessPercent = useBusinessToolStore(getAIAssistantReadinessPercent);
  const { showModal, hideModal } = useModalContext();
  const aiErrors = useAiErrors();
  const { t } = useTranslation();

  const orderTableValues = useOrderTableValues();

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<IAIAssistantError | null>(null);

  const timeoutRef = useRef<number | null>(null);

  const { setValue, getValues, setFocus, clearErrors } = useFormContext();
  const getUpdatedProductCalculation = useGetCalculationsOnProductUpdate();

  const businessPlanType = useBusinessToolStore(getBusinessPlanType);
  const businessPlanChapters = useBusinessToolStore(getBusinessPlanChapters);
  const products = businessPlanChapters.GLOBAL[GLOBAL_FIELD_NAMES.PRODUCTS];
  const ourCompanyName = businessPlanChapters.GLOBAL[GLOBAL_FIELD_NAMES.COMPANY_NAME];

  const createProduct = useCreateProduct(businessPlanId);
  const deleteProduct = useDeleteProduct(businessPlanId);

  const setComplexFields = async (name: string, parsedResponse: Record<string, any>, regenerate: boolean = false) => {
    switch (name) {
      case 'ABOUT_COMPANY.swot': {
        setValue('GLOBAL.companyStrengths', parsedResponse.companyStrengths);
        setValue('GLOBAL.companyWeaknesses', parsedResponse.companyWeaknesses);
        setValue(`${name}.opportunities`, parsedResponse.opportunities);
        setValue(`${name}.threats`, parsedResponse.threats);
        break;
      }
      case 'MARKET_ANALYSIS.consumerNeeds.B2B': {
        const data = parsedResponse.keyConsumerProfile;
        setValue('MARKET_ANALYSIS.consumerNeeds.B2B', {
          ...(data || parsedResponse),
        });
        break;
      }
      case `${marketAnalysisPrefix}.${COMPETITIVE_ANALYSIS_NAMES.COMPETITORS_QUALITIES}`: {
        const ourCompanyData = parsedResponse.ourCompany;
        const competitorsData = parsedResponse.competitors;
        setValue(
          `${marketAnalysisPrefix}.${COMPETITIVE_ANALYSIS_NAMES.YOUR_COMPANY_QUALITIES}.${COMPANY_QUALITIES_NAMES.STRENGTH}`,
          ourCompanyData?.strength,
        );
        setValue(
          `${marketAnalysisPrefix}.${COMPETITIVE_ANALYSIS_NAMES.YOUR_COMPANY_QUALITIES}.${COMPANY_QUALITIES_NAMES.WEAKNESSES}`,
          ourCompanyData?.weaknesses,
        );
        setValue(
          `${marketAnalysisPrefix}.${COMPETITIVE_ANALYSIS_NAMES.YOUR_COMPANY_QUALITIES}.${COMPANY_QUALITIES_NAMES.NAME}`,
          ourCompanyName,
        );
        setValue(`${marketAnalysisPrefix}.${COMPETITIVE_ANALYSIS_NAMES.COMPETITORS_QUALITIES}`, competitorsData);
        break;
      }
      case 'GLOBAL.products': {
        let productsArray: any[];
        let orderTableValuesArray: any[];
        if (regenerate) {
          await Promise.all(
            (products ?? []).map(async (product, index) => {
              const id = product.productId;
              if (id) {
                await deleteProduct.mutateAsync({ id });
              }
              return index;
            }),
          );

          productsArray = [...(Array.isArray(parsedResponse) ? parsedResponse : [])];
          orderTableValuesArray = [];
        } else {
          const filteredFromEmptyProducts = products?.filter((item) => {
            const productKeysArrayWithoutIdKeys = Object.keys(item).filter(
              (key) => key !== 'productId' && key !== 'volumeUnits',
            );
            if (productKeysArrayWithoutIdKeys.every((key) => !(item as any)[key])) {
              const id = item.productId;
              if (id) {
                deleteProduct.mutateAsync({ id });
              }
              return false;
            }
            return true;
          });

          if (filteredFromEmptyProducts?.length === 5) {
            break;
          }
          orderTableValuesArray = [...(filteredFromEmptyProducts ?? [])];
          productsArray = [
            ...(filteredFromEmptyProducts ?? []),
            ...(Array.isArray(parsedResponse) ? parsedResponse : []),
          ].slice(0, 5);
        }
        await new Promise((resolve) => setTimeout(resolve, 500)); // awaiting for deleting empty products before creating new
        Promise.all(
          productsArray.map(async (data: any) => {
            if (data?.productId) {
              const product = {
                ...data,
                name: data.name ?? data.productName,
                price: data.price ?? data.productPrice,
              };

              return product;
            }

            const { id: productId } = await createProduct.mutateAsync(businessPlanId);

            const product = {
              ...data,
              productId,
            };

            return product;
          }),
        )
          .then((data) => {
            setValue('GLOBAL.products', data);

            // this code is needed because when we fill the 'Provide the sales plan broken down by products/services' with generated products the FormChangeObserver doesn't see this changes and CalculationsSectionObserver doesn't trigger calculation functions
            data.forEach(async (product: any) => {
              if (!isAllSalesPlanRequiredFieldsFilled(product)) return;

              const body = {
                unit: DIMENSIONALITY_VALUES.SINGLE,
                id: product.productId || '',
                businessPlanId,
                product: getProductFieldsForSalesPlan(product),
              };
              const updatedCalculation = await getUpdatedProductCalculation.mutateAsync(body);

              setValue(
                'MARKETING_AND_SALES.salesPlan.plannedProductsVolume',
                orderTableValues(orderTableValuesArray, updatedCalculation.revenueData),
              );
              setValue('MARKETING_AND_SALES.salesPlan.totalPlannedProductsVolume', updatedCalculation.totalRevenueData);
            });
          })
          .catch((err) => ToastService.showError(err.message));

        break;
      }
      case 'FINANCIAL_PLAN.plannedExpenses.monthlyExpenses': {
        const unit = getValues(GLOBAL_UNIT_NAME);
        const multiplier = unitConvertorMultiplier[unit][DIMENSIONALITY_VALUES.SINGLE];

        const totalFirstMonthRevenue =
          getValues('FINANCIAL_PLAN.revenuePlan.totalPlannedProductsVolume.monthValues.m1') * multiplier;

        setValue('FINANCIAL_PLAN.plannedExpenses.monthlyExpenses', {
          ...parsedResponse,
        });

        Object.entries(parsedResponse).forEach(([field, value]) => {
          if (field.endsWith('Revenue')) {
            const expenseField = field.replace('Revenue', 'Expenses');

            const calculatedExpense = Number(totalFirstMonthRevenue) * (Number(value) / 100) || '';

            setValue(`FINANCIAL_PLAN.plannedExpenses.monthlyExpenses.${expenseField}`, calculatedExpense);
          }
        });
        break;
      }
      default:
        setValue(name, parsedResponse);
    }
    setLoading(false);
  };

  const typeResponseText = async (response: string, fieldName: string) => {
    let currentText = '';

    setFocus(fieldName);
    const typeText: TimerHandler = () => {
      if (currentText.length < response.length) {
        currentText += response.charAt(currentText.length);
        setValue(fieldName, currentText);
        timeoutRef.current = setTimeout(typeText, speed);
      } else {
        setLoading(false);
        timeoutRef.current = null;
      }
    };
    typeText();
  };

  const shouldUpdateComplexField = (name: string): boolean => {
    if (!getValues(name)) {
      return true;
    } else if (Array.isArray(getValues(name)) && getValues(name).length === 0) {
      return true;
    } else if (name === 'ABOUT_COMPANY.swot') {
      if (
        !Object.values(getValues(name)).some((value) => (value as any[])?.some((item) => Boolean(item.title))) &&
        ![
          Object.values(getValues('GLOBAL.companyStrengths')),
          Object.values(getValues('GLOBAL.companyWeaknesses')),
        ].some((value) => (value as any[])?.some((item) => Boolean(item.title)))
      ) {
        return true;
      }
    } else if (name === 'ABOUT_COMPANY.missionAndStrategy.goals') {
      if (
        !getValues(name).some(
          (item: IGoalAndObjectiveRowForm) => Boolean(item?.goal) || Boolean(item?.mechanismOfAchievement),
        )
      ) {
        return true;
      }
    } else if (name === 'MARKET_ANALYSIS.marketOverview.marketTrends') {
      if (
        !getValues(name).some(
          (item: IMarketTrendsForm) =>
            Boolean(item?.[MARKET_TRENDS_FIELD_NAMES.TREND_NAME]) ||
            Boolean(item?.[MARKET_TRENDS_FIELD_NAMES.TREND_CHARACTERISTICS]),
        )
      ) {
        return true;
      }
    } else if (name === 'MARKET_ANALYSIS.competitiveAnalysis.keyCompetitors') {
      if (
        !getValues(name).some((item: IKeyCompetitors) => {
          // eslint-disable-next-line @typescript-eslint/no-unused-vars
          const { file, customerLoyaltyLevel, ...rest } = item;
          return Boolean(file?.length) || Object.values(rest).some(Boolean);
        })
      ) {
        return true;
      }
    } else if (name === 'MARKET_ANALYSIS.competitiveAnalysis.competitorsQualities') {
      if (
        !getValues(name).some((item: ICompanyQualities) => {
          const { name, strength, weaknesses } = item;
          return (
            Boolean(name) ||
            strength.some((item) => Boolean(item?.[STRENGTH_NAMES.STRENGTH])) ||
            weaknesses.some((item) => Boolean(item?.[WEAKNESSES_NAMES.WEAKNESSES]))
          );
        }) &&
        (getValues('MARKET_ANALYSIS.competitiveAnalysis.yourCompanyQualities') as ICompanyQualities).strength.every(
          (item) => !item?.[STRENGTH_NAMES.STRENGTH],
        ) &&
        (getValues('MARKET_ANALYSIS.competitiveAnalysis.yourCompanyQualities') as ICompanyQualities).weaknesses.every(
          (item) => !item?.[WEAKNESSES_NAMES.WEAKNESSES],
        )
      ) {
        return true;
      }
    } else if (name === 'MARKET_ANALYSIS.consumerNeeds.B2C') {
      if (
        Object.entries(getValues(name))
          .filter((item) => item[0] !== B2C_NAMES.GENDER)
          .every((item) => !item[1] || item[1] === DEFAULT_VALUE)
      ) {
        return true;
      }
    } else if (name === 'MARKET_ANALYSIS.consumerNeeds.B2B') {
      if (Object.values(getValues(name)).every((item) => !item || item === DEFAULT_VALUE)) {
        return true;
      }
    } else if (name === 'MARKETING_AND_SALES.salesPlan.stages') {
      if (getValues(name).every((item: ISalesStages) => Object.values(item).every((value) => !value))) {
        return true;
      }
    } else if (name === 'MARKETING_AND_SALES.salesPlan.salesChannels') {
      if (
        getValues(name).every((item: IChannels) =>
          Object.values(item).every((value) => !value || value === DEFAULT_VALUE),
        )
      ) {
        return true;
      }
    } else if (name === 'LEGAL_FRAMEWORKS_AND_RISKS.listOfRisks') {
      if (
        getValues(name).every((item: IListOfRisks) =>
          Object.values(item).every((value) => {
            if (typeof value === 'string') {
              return !value;
            } else if (typeof value === 'object') {
              return Object.values(value).every((value) => !value);
            }
          }),
        )
      ) {
        return true;
      }
    } else if (name === 'FINANCIAL_PLAN.plannedExpenses.monthlyExpenses') {
      // eslint-disable-next-line @typescript-eslint/no-unused-vars
      const { nonOperatingExpenses, nonOperatingRevenue, ...monthlyExpenses } = getValues(name);
      if (Object.values(monthlyExpenses).every((value) => !value || value === DEFAULT_VALUE)) {
        return true;
      }
    } else if (name === 'GLOBAL.products') {
      return true;
    }

    return false;
  };

  const executeApiFunction = async (
    fieldNames: string[],
    aiAssistantType: IAIAssistantType,
    isSectionGeneration: boolean,
    useCustomModel: boolean,
  ) => {
    setError(null);

    const names = fieldNames.map((name) => name);

    const apiFunction = aiOption[aiAssistantType];

    const responses = await apiFunction({
      id: businessPlanId,
      names,
      blocks: fieldNames,
      requestParams: isSectionGeneration ? fieldNames : names,
      text: getValues(fieldNames[0]),
      formData: getValues(),
      useCustomModel,
    });

    if (responses.length === 0) {
      ToastService.showInfo('No data to generate');
    }
    fieldNames.forEach(async (name, index) => {
      await asyncDelay(index * 40);
      const response = responses.find((item) => name === item.chapter);
      const responseText = response?.message;

      if (!responseText) {
        return;
      }

      clearErrors(name);

      // Determine if responseText is JSON and parse if necessary
      let parsedResponse;
      try {
        parsedResponse = JSON.parse(responseText.replace('```json', '').replace('```', '')); // Attempt to parse JSON
      } catch (error) {
        parsedResponse = responseText;
      }
      // Handle response based on its type after parsing
      if (isTypingAnimationEnabled && typeof parsedResponse === 'string' && fieldType === AI_RESPONSE_TYPES.TEXT) {
        await typeResponseText(parsedResponse, name);
      } else if (fieldType === AI_RESPONSE_TYPES.COMPLEX) {
        setComplexFields(name, parsedResponse, true);
      } else {
        if (!getValues(name)) {
          setValue(name, parsedResponse);
        }

        if (typeof parsedResponse === 'object' && shouldUpdateComplexField(name)) {
          setComplexFields(name, parsedResponse);
        }
      }
    });
  };

  const handleApiError = (error: unknown, isFieldsArray: boolean) => {
    if (isProgrammaticAIRequestAbort(error)) {
      return;
    }

    if (error instanceof Error) {
      let key: IAIAssistantErrorTypes = AI_ASSISTANT_ERRORS.DEFAULT;
      if (
        error.message === AI_ASSISTANT_ERRORS.NOT_ENOUGH_CONTEXT &&
        (businessPlanType === BUSINESS_PLAN_TYPES.ONE_PAGE || businessPlanType === BUSINESS_PLAN_TYPES.ONE_PAGE_SHORT)
      ) {
        key = AI_ASSISTANT_ERRORS.NOT_ENOUGH_CONTEXT_FOR_ONE_PAGE;
        showModal(AIGenerationErrorModalParent, { close: hideModal, errorMessage: 'NOT_ENOUGH_CONTEXT' });
      } else if (error.message === AI_ASSISTANT_ERRORS.NOT_ENOUGH_CONTEXT) {
        showModal(AIGenerationErrorModalParent, { close: hideModal, errorMessage: 'NOT_ENOUGH_CONTEXT' });
      } else {
        key = error.message as IAIAssistantErrorTypes;
        isFieldsArray && showModal(AIGenerationErrorModalParent, { close: hideModal, errorMessage: 'SOME_ERROR' });
      }
      const errorObject = aiErrors[key];
      setError(
        errorObject ?? createAIErrorObject(t('aiGenerationErrors.error'), t('aiGenerationErrors.somethingWentWrong')),
      );
    } else {
      setError(aiErrors[AI_ASSISTANT_ERRORS.DEFAULT]);
    }
  };

  const handleApiRequest = async (aiAssistantType: IAIAssistantType, isSectionGeneration: boolean = false) => {
    if (!fieldName) {
      return;
    }
    setLoading(true);

    const fieldNames = Array.isArray(fieldName) ? fieldName : [fieldName];
    const values = fieldNames.map((name) => ({ name, value: getValues(name) }));
    const isMarketOverviewOrCompetitiveAnalysis = checkIsMarketOverviewOrCompetitiveAnalysisField(fieldName);

    try {
      if (!businessPlanId) {
        throw new Error(AI_ASSISTANT_ERRORS.MISSING_BUSINESS_PLAN_ID);
      }

      // Throw error if any field required for operation is missing and not in generate mode
      values.forEach(({ value }) => {
        if (aiAssistantType === AI_ASSISTANT_ITEMS.IMPROVE) {
          if (!value) {
            throw new Error(AI_ASSISTANT_ERRORS.MISSING_CONTENT);
          }
        } else if (aiAssistantType === AI_ASSISTANT_ITEMS.GENERATE && AIAssistantReadinessPercent !== 100) {
          throw new Error(AI_ASSISTANT_ERRORS.NOT_ENOUGH_CONTEXT);
        } else if (!value && aiAssistantType !== AI_ASSISTANT_ITEMS.GENERATE) {
          throw new Error(AI_ASSISTANT_ERRORS.MISSING_CONTENT);
        }
      });

      await executeApiFunction(fieldNames, aiAssistantType, isSectionGeneration, isMarketOverviewOrCompetitiveAnalysis);
    } catch (error) {
      values.forEach(({ name, value }) => {
        if (!getValues(name)) {
          setValue(name, value);
        }
      });
      handleApiError(error, Array.isArray(fieldName));
      setLoading(false);
    }
  };

  const resetError = () => setError(null);

  return { handleApiRequest, loading, error, resetError };
};
